import { safeErr, isValidEmail, isValidPhone } from '../App';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getContacts, getContactsCount, createContact, deleteContact, starContact, bulkLeadsToContacts, getLeads, generateDrafts, getContactAddedByList, importContactsFile } from '../api';

const SECTORS = ['IT & Software','Healthcare','Manufacturing','Retail','Education','Logistics','Finance','Real Estate','Food & Agri'];
const AV_COLORS = ['av-blue','av-green','av-amber','av-purple','av-red'];
const PAGE_SIZE = 24;

// ── Add Contact Modal ──────────────────────────────────────────────────────────
function AddModal({ onClose, onSaved, showToast }) {
  const [f, setF] = useState({name:'',email:'',phone:'',company:'',role:'',sector:'',location:'',website:'',linkedin:'',tags:'',notes:''});
  const [saving, setSave] = useState(false);
  const [err, setErr]     = useState('');
  const s = (k,v) => setF(p=>({...p,[k]:v}));

  const save = async () => {
    if (!f.name||!f.email) { setErr('Name and email are required'); return; }
    if (!isValidEmail(f.email)) { setErr('Enter a valid email address'); return; }
    if (!isValidPhone(f.phone)) { setErr('Enter a valid phone number'); return; }
    setSave(true); setErr('');
    try { await createContact(f); onSaved(); }
    catch(e) { setErr(safeErr(e,'Error saving')); }
    finally { setSave(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" style={{width:540}}>
        <div className="modal-header">
          <span className="modal-title">Add new contact</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div className="alert alert-error">{err}</div>}
          <div className="form-row">
            <div className="form-group"><label className="form-label">Full name *</label><input className="form-input" value={f.name} onChange={e=>s('name',e.target.value)} placeholder="Rajesh Kumar"/></div>
            <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" value={f.email} onChange={e=>s('email',e.target.value)} placeholder="rajesh@company.com"/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={f.phone} onChange={e=>s('phone',e.target.value)} placeholder="+91 98765 43210"/></div>
            <div className="form-group"><label className="form-label">Company</label><input className="form-input" value={f.company} onChange={e=>s('company',e.target.value)} placeholder="Acme Corp"/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Role / Title</label><input className="form-input" value={f.role} onChange={e=>s('role',e.target.value)} placeholder="CEO"/></div>
            <div className="form-group"><label className="form-label">Sector</label>
              <select className="form-select" value={f.sector} onChange={e=>s('sector',e.target.value)}>
                <option value="">Select sector</option>
                {SECTORS.map(x=><option key={x}>{x}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Location</label><input className="form-input" value={f.location} onChange={e=>s('location',e.target.value)} placeholder="Chennai, TN"/></div>
            <div className="form-group"><label className="form-label">Tags (comma separated)</label><input className="form-input" value={f.tags} onChange={e=>s('tags',e.target.value)} placeholder="client, vip, warm"/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Website</label><input className="form-input" value={f.website} onChange={e=>s('website',e.target.value)} placeholder="https://company.com"/></div>
            <div className="form-group"><label className="form-label">LinkedIn</label><input className="form-input" value={f.linkedin} onChange={e=>s('linkedin',e.target.value)} placeholder="linkedin.com/in/…"/></div>
          </div>
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" value={f.notes} onChange={e=>s('notes',e.target.value)} placeholder="Notes about this contact…" style={{minHeight:64}}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving?'Saving…':'Save contact'}</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Import from Leads Modal ────────────────────────────────────────────────────
function ImportModal({ onClose, onSaved }) {
  const [leads, setLeads]   = useState([]);
  const [sel, setSel]       = useState(new Set());
  const [loading, setLoad]  = useState(true);
  const [saving, setSave]   = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getLeads().then(r => {
      setLeads(r.data);
      setSel(new Set(r.data.map(l=>l.id)));
    }).finally(() => setLoad(false));
  }, []);

  const toggleAll = () => setSel(sel.size===leads.length ? new Set() : new Set(leads.map(l=>l.id)));
  const toggle    = id => setSel(p => { const n=new Set(p); n.has(id)?n.delete(id):n.add(id); return n; });

  const doImport = async () => {
    setSave(true);
    const r = await bulkLeadsToContacts([...sel]);
    setResult(r.data);
    setSave(false);
    setTimeout(onSaved, 1800);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" style={{width:600}}>
        <div className="modal-header">
          <span className="modal-title">Import leads as contacts</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div style={{flex:1,overflow:'auto',maxHeight:'55vh'}}>
          {loading ? <div className="loading-center"><div className="spinner spinner-md"/></div>
          : result ? (
            <div style={{padding:32,textAlign:'center'}}>
              <div className="alert alert-success" style={{justifyContent:'center',fontSize:15}}>
                ✓ {result.added} contacts imported{result.skipped?`, ${result.skipped} already existed`:''}
              </div>
            </div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr>
                <th style={{padding:'9px 14px',background:'var(--surface2)',borderBottom:'1px solid var(--border)',textAlign:'left',width:36}}>
                  <input type="checkbox" checked={sel.size===leads.length&&leads.length>0}
                    onChange={toggleAll} style={{accentColor:'var(--blue)',cursor:'pointer'}}/>
                </th>
                {['Name','Company','Sector','Status'].map(h=>(
                  <th key={h} style={{padding:'9px 14px',background:'var(--surface2)',borderBottom:'1px solid var(--border)',textAlign:'left',fontSize:10,fontWeight:600,color:'var(--text-2)',textTransform:'uppercase',letterSpacing:'.05em'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {leads.map(l=>(
                  <tr key={l.id} onClick={()=>toggle(l.id)} style={{cursor:'pointer',background:sel.has(l.id)?'var(--blue-bg)':''}}>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid var(--border)'}}>
                      <input type="checkbox" checked={sel.has(l.id)} onChange={()=>toggle(l.id)}
                        style={{accentColor:'var(--blue)',cursor:'pointer'}}/>
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid var(--border)',fontWeight:600}}>{l.name}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid var(--border)'}}>{l.company}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid var(--border)',color:'var(--text-2)',fontSize:12}}>{l.sector}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid var(--border)'}}><span className={`pill s-${l.status.split('_')[0]==='followup'?'followup':l.status}`} style={{fontSize:10}}>{l.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {!result && (
          <div className="modal-footer">
            <button className="btn btn-primary" disabled={saving||sel.size===0} onClick={doImport}>
              {saving?'Importing…':`Import ${sel.size} contacts`}
            </button>
            <button className="btn" onClick={onClose}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Import from File Modal ─────────────────────────────────────────────────────
function ImportFileModal({ onClose, onSaved }) {
  const [file, setFile]     = useState(null);
  const [saving, setSave]   = useState(false);
  const [err, setErr]       = useState('');
  const [result, setResult] = useState(null);

  const doImport = async () => {
    if (!file) { setErr('Choose a CSV file first'); return; }
    setSave(true); setErr('');
    try {
      const r = await importContactsFile(file);
      setResult(r.data);
      setTimeout(onSaved, 1800);
    } catch(e) { setErr(safeErr(e,'Import failed')); }
    finally { setSave(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" style={{width:480}}>
        <div className="modal-header">
          <span className="modal-title">Import contacts from file</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div className="alert alert-error">{err}</div>}
          {result ? (
            <div style={{padding:'8px 0'}}>
              <div className="alert alert-success" style={{justifyContent:'center',fontSize:15}}>
                ✓ {result.added} contacts imported
                {result.skipped ? `, ${result.skipped} duplicates skipped` : ''}
                {result.invalid ? `, ${result.invalid} invalid rows skipped` : ''}
              </div>
              {result.errors && result.errors.length > 0 && (
                <div style={{marginTop:10,fontSize:12,color:'var(--text-2)',maxHeight:140,overflow:'auto'}}>
                  {result.errors.map((e,i)=><div key={i}>{e}</div>)}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">CSV file</label>
                <input type="file" accept=".csv,text/csv" className="form-input"
                  onChange={e=>setFile(e.target.files[0]||null)}/>
              </div>
              <div style={{fontSize:12,color:'var(--text-2)'}}>
                Required columns: <b>name</b>, <b>email</b>. Optional: phone, company, role, sector, location, website, linkedin, tags, notes.
              </div>
            </>
          )}
        </div>
        {!result && (
          <div className="modal-footer">
            <button className="btn btn-primary" disabled={saving||!file} onClick={doImport}>
              {saving?'Importing…':'Import contacts'}
            </button>
            <button className="btn" onClick={onClose}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Contacts page ─────────────────────────────────────────────────────────
export default function Contacts({ showToast }) {
  const [contacts, setContacts] = useState([]);
  const [counts,   setCounts]   = useState({total:0,starred:0,from_lead:0,emailed:0});
  const [search,   setSearch]   = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sector,   setSector]   = useState('');
  const [emailed,  setEmailed]  = useState('');
  const [addedBy,  setAddedBy]  = useState('');
  const [adders,   setAdders]   = useState([]);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoad]     = useState(true);
  const [showAdd,  setAdd]      = useState(false);
  const [showImp,  setImp]      = useState(false);
  const [showImpFile, setImpFile] = useState(false);
  const [drafting, setDrafting] = useState({});
  const nav = useNavigate();

  const totalPages = Math.max(1, Math.ceil(counts.total / PAGE_SIZE));

  // Debounce search input
  useEffect(()=>{ const t=setTimeout(()=>setDebouncedSearch(search),350); return()=>clearTimeout(t); },[search]);
  // Any filter change jumps back to page 1
  useEffect(()=>{ setPage(1); },[debouncedSearch,sector,emailed,addedBy]);

  useEffect(() => { getContactAddedByList().then(r => setAdders(r.data)); }, []);

  const load = () => {
    setLoad(true);
    const params = {search:debouncedSearch, sector, emailed, added_by:addedBy, limit:PAGE_SIZE, offset:(page-1)*PAGE_SIZE};
    Promise.all([getContacts(params), getContactsCount({search:debouncedSearch,sector,emailed,added_by:addedBy})])
      .then(([r, c]) => { setContacts(r.data); setCounts(c.data); })
      .finally(()=>setLoad(false));
  };
  useEffect(load,[page,sector,emailed,addedBy,debouncedSearch]);

  const handleDraft = async (id,e) => {
    e.stopPropagation();
    setDrafting(p=>({...p,[id]:true}));
    try {
      const r = await generateDrafts([id]);
      if (r.data.created>0) showToast('Draft generated — check AI Drafts to review');
      else showToast('Could not generate a draft for this contact','error');
    } catch(e) { showToast(safeErr(e,'Failed to generate draft'),'error'); }
    finally { setDrafting(p=>({...p,[id]:false})); }
  };

  const goToPage = p => { if (p>=1 && p<=totalPages) setPage(p); };

  const handleStar = async (id,e) => { e.stopPropagation(); await starContact(id); load(); };
  const handleDel  = async (id,e) => {
    e.stopPropagation();
    if(!window.confirm('Delete contact?')) return;
    await deleteContact(id);
    showToast('Contact deleted');
    // If that was the last contact on this page (and not page 1), step back a page
    if (contacts.length===1 && page>1) setPage(page-1);
    else load();
  };

  const initials = name => name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();

  return (
    <>
      {showAdd && <AddModal onClose={()=>setAdd(false)} onSaved={()=>{setAdd(false);showToast('Contact saved!');load();}} showToast={showToast}/>}
      {showImp && <ImportModal onClose={()=>setImp(false)} onSaved={()=>{setImp(false);showToast('Contacts imported!');load();}}/>}
      {showImpFile && <ImportFileModal onClose={()=>setImpFile(false)} onSaved={()=>{setImpFile(false);showToast('Contacts imported!');load();}}/>}

      {/* Toolbar */}
      <div style={{display:'flex',gap:8,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
        <input className="form-input" style={{width:220}} placeholder="Search contacts…"
          value={search} onChange={e=>setSearch(e.target.value)}/>
        <select className="form-select" style={{width:160}} value={sector} onChange={e=>setSector(e.target.value)}>
          <option value="">All sectors</option>
          {SECTORS.map(s=><option key={s}>{s}</option>)}
        </select>
        <select className="form-select" style={{width:160}} value={emailed} onChange={e=>setEmailed(e.target.value)}>
          <option value="">Emailed: All</option>
          <option value="yes">Already emailed</option>
          <option value="no">Not emailed yet</option>
        </select>
        {adders.length > 0 && (
          <select className="form-select" style={{width:150}} value={addedBy} onChange={e=>setAddedBy(e.target.value)}>
            <option value="">Added by: anyone</option>
            {adders.map(a=><option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <div style={{marginLeft:'auto',display:'flex',gap:8}}>
          <button className="btn btn-sm" onClick={()=>setImpFile(true)}>↑ Import from file</button>
          <button className="btn btn-sm" onClick={()=>setImp(true)}>↓ Import from leads</button>
          <button className="btn btn-primary btn-sm" onClick={()=>setAdd(true)}>+ Add contact</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:16}}>
        {[['Total contacts',counts.total],['Starred',counts.starred],['From leads',counts.from_lead],['Emailed',counts.emailed]].map(([l,v])=>(
          <div key={l} className="metric-card" style={{marginBottom:0}}>
            <div className="metric-label">{l}</div>
            <div className={`metric-value`}>{v}</div>
          </div>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg"/></div>
      ) : contacts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <div className="empty-title">No contacts yet</div>
          <div className="empty-desc">Add contacts manually or import from your leads</div>
          <div style={{display:'flex',gap:8,justifyContent:'center'}}>
            <button className="btn btn-primary" onClick={()=>setAdd(true)}>+ Add contact</button>
            <button className="btn" onClick={()=>setImp(true)}>↓ Import from leads</button>
            <button className="btn" onClick={()=>setImpFile(true)}>↑ Import from file</button>
          </div>
        </div>
      ) : (
        <div className="contacts-grid">
          {contacts.map((c,i) => (
            <div key={c.id} className="contact-card" onClick={()=>nav(`/contacts/${c.id}`)}>
              <div style={{display:'flex',alignItems:'flex-start',gap:11,marginBottom:12}}>
                <div className={`avatar av-md ${AV_COLORS[i%AV_COLORS.length]}`} style={{flexShrink:0}}>
                  {initials(c.name)}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:13,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</div>
                  <div style={{fontSize:11,color:'var(--text-2)',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {[c.role,c.company].filter(Boolean).join(' · ') || 'No role/company'}
                  </div>
                </div>
                <button style={{background:'none',border:'none',fontSize:17,cursor:'pointer',color:c.starred?'#D97706':'var(--text-3)',flexShrink:0,padding:'0 2px',lineHeight:1}}
                  onClick={e=>handleStar(c.id,e)}>{c.starred?'★':'☆'}</button>
              </div>

              <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:c.emailed||c.next_followup?8:0}}>
                {c.emailed && <span className="pill pill-blue" style={{fontSize:10}}>✉ Emailed</span>}
                {c.next_followup && (
                  <span className="pill pill-amber" style={{fontSize:10}}>
                    ↻ Follow-up {(c.followup_count||0)+1} · {new Date(c.next_followup).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}
                  </span>
                )}
                {c.interest==='interested' && <span className="pill pill-green" style={{fontSize:10}}>Interested</span>}
                {c.interest==='not_interested' && <span className="pill pill-red" style={{fontSize:10}}>Not interested</span>}
              </div>

              <div style={{display:'flex',flexDirection:'column',gap:5,marginBottom:10}}>
                <div style={{fontSize:12,color:'var(--text-2)',display:'flex',gap:6}}>
                  <span>✉</span>
                  <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.email}</span>
                </div>
                {c.phone && <div style={{fontSize:12,color:'var(--text-2)',display:'flex',gap:6}}><span>📞</span>{c.phone}</div>}
                {c.location && <div style={{fontSize:12,color:'var(--text-2)',display:'flex',gap:6}}><span>📍</span>{c.location}</div>}
                {c.added_by && <div style={{fontSize:12,color:'var(--text-2)',display:'flex',gap:6}}><span>👤</span>Added by {c.added_by}</div>}
              </div>

              {c.tags && (
                <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:10}}>
                  {c.tags.split(',').filter(Boolean).map(t=>{
                    const isClient = t.trim().toLowerCase()==='client';
                    return (
                      <span key={t} style={isClient
                        ? {background:'var(--green-bg)',border:'1px solid var(--green-border)',borderRadius:20,padding:'1px 8px',fontSize:11,fontWeight:600,color:'var(--green)'}
                        : {background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:20,padding:'1px 8px',fontSize:11,color:'var(--text-2)'}}>
                        {isClient ? '✓ Client' : t.trim()}
                      </span>
                    );
                  })}
                </div>
              )}

              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                  {c.source==='lead' && <span className="pill pill-blue" style={{fontSize:10}}>From lead</span>}
                  {c.sector && <span style={{fontSize:11,color:'var(--text-2)'}}>{c.sector}</span>}
                </div>
                <div style={{display:'flex',gap:5}}>
                  <button className="btn btn-xs" title="Generate AI draft" disabled={drafting[c.id]}
                    onClick={e=>handleDraft(c.id,e)}>
                    {drafting[c.id] ? <span className="spinner spinner-sm"/> : '✦ Draft'}
                  </button>
                  <button className="btn btn-primary btn-xs" title="Send email"
                    onClick={e=>{e.stopPropagation();nav(`/email-manager?compose=1&to=${c.email}&name=${encodeURIComponent(c.name)}&cid=${c.id}`);}}>
                    ✉ Email
                  </button>
                  <button className="btn btn-danger btn-xs" onClick={e=>handleDel(c.id,e)}>✕</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && counts.total > 0 && (
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:16,flexWrap:'wrap',gap:8}}>
          <div style={{fontSize:12,color:'var(--text-2)'}}>
            Showing {(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE,counts.total)} of {counts.total}
          </div>
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            <button className="btn btn-sm" disabled={page<=1} onClick={()=>goToPage(page-1)}>← Prev</button>
            <span style={{fontSize:12,color:'var(--text-2)',padding:'0 6px'}}>Page {page} of {totalPages}</span>
            <button className="btn btn-sm" disabled={page>=totalPages} onClick={()=>goToPage(page+1)}>Next →</button>
          </div>
        </div>
      )}
    </>
  );
}
